import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { faComments, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { ChatService } from '../../services/chat';
import { UploadService } from '../../services/upload/upload.service';
import { NotificationService } from '../../services/notification';
import { BrowserNotificationService } from '../../services/browser-notification';
import { MessageType, SenderType, SolicitationMessage } from '../../models/SolicitationMessage';
import { IAppState } from '../../store/interfaces/IAppState';
import { selectToken } from '../../store/selectors';

@Component({
  selector: 'app-chat-solicitacao',
  templateUrl: './chat-solicitacao.component.html',
  styleUrls: ['./chat-solicitacao.component.scss'],
})
export class ChatSolicitacaoComponent implements OnInit, OnChanges, OnDestroy {
  /** Código da solicitação (callcode). */
  @Input() callcode!: string;

  /** Quando true, exibe somente leitura (ex.: solicitação encerrada). */
  @Input() readOnly = false;

  /** Quando true, renderiza na visão do analista (alinhamento e identidade). */
  @Input() isAnalyst = false;

  /** Quando true, o chat cresce para preencher a altura disponível do container. */
  @Input() fillHeight = false;

  @ViewChild('messagesContainer') private messagesContainer?: ElementRef<HTMLDivElement>;

  messages: SolicitationMessage[] = [];
  draft = '';
  sending = false;

  readonly SenderType = SenderType;
  readonly MessageType = MessageType;

  faComments = faComments;
  faPaperclip = faPaperclip;

  /** Mensagens recebidas enquanto a aba estava sem foco (para título/notificação). */
  private unreadWhileAway = 0;

  private destroyed$ = new Subject<void>();

  constructor(
    private chatService: ChatService,
    private uploadService: UploadService,
    private notification: NotificationService,
    private browserNotification: BrowserNotificationService,
    private store: Store<IAppState>
  ) {}

  ngOnInit(): void {
    this.browserNotification.requestPermission();
    this.initChat();
  }

  /** Aba voltou a ter foco/visibilidade -> marca lido e limpa notificações. */
  @HostListener('window:focus')
  @HostListener('document:visibilitychange')
  onWindowActive(): void {
    if (this.isActive()) {
      this.handleBecameActive();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['callcode'] && !changes['callcode'].firstChange) {
      this.initChat();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.browserNotification.clearTitle();
    this.chatService.disconnect();
  }

  /** Uma mensagem é "minha" conforme o papel de quem está visualizando o chat. */
  isMine(message: SolicitationMessage): boolean {
    return this.isAnalyst
      ? message.senderType === SenderType.Analista
      : message.senderType === SenderType.Cliente;
  }

  onSubmit(): void {
    const text = this.draft.trim();
    if (!text || this.readOnly || this.sending) {
      return;
    }

    this.sending = true;
    this.chatService
      .sendMessage({ callcode: this.callcode, text })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.draft = '';
        },
        error: (err) => this.notification.error(err?.error?.message ?? 'Não foi possível enviar a mensagem.'),
      })
      .add(() => (this.sending = false));
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || this.readOnly || this.sending) {
      return;
    }

    this.sending = true;
    this.uploadService
      .uploadFiles([file])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (urls) => {
          const fileUrl = urls?.[0];
          if (!fileUrl) {
            this.notification.error('Falha no envio do arquivo.');
            return;
          }
          this.chatService
            .sendMessage({
              callcode: this.callcode,
              fileName: file.name,
              fileType: file.type,
              fileUrl,
            })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
              error: (err) =>
                this.notification.error(err?.error?.message ?? 'Não foi possível enviar o arquivo.'),
            });
        },
        error: () => this.notification.error('Falha no envio do arquivo.'),
      })
      .add(() => {
        this.sending = false;
        input.value = '';
      });
  }

  private async initChat(): Promise<void> {
    if (!this.callcode) {
      return;
    }

    // Garante que o token já está no store antes de qualquer chamada à API
    // (no bypass do analista o token é gravado no store ao abrir a aba).
    await firstValueFrom(this.store.select(selectToken).pipe(filter((t): t is string => !!t), take(1)));

    this.chatService.getMessages(this.callcode).pipe(takeUntil(this.destroyed$)).subscribe((messages) => {
      this.messages = messages;
      this.scrollToBottom();
      if (this.isActive()) {
        this.markAsRead();
      }
    });

    this.chatService.onMessageReceived
      .pipe(
        filter((message) => message.callcode === this.callcode),
        takeUntil(this.destroyed$)
      )
      .subscribe((message) => {
        this.messages = [...this.messages, message];
        this.scrollToBottom();

        if (this.isMine(message)) {
          return;
        }

        if (this.isActive()) {
          // Aba em foco: leitura imediata (estilo WhatsApp).
          this.markAsRead();
        } else {
          // Aba aberta sem foco: acumula, atualiza título e notifica o SO.
          this.unreadWhileAway += 1;
          this.browserNotification.setUnreadCount(this.unreadWhileAway);
          this.browserNotification.notify(
            `Nova mensagem — Solicitação ${this.callcode}`,
            message.text || (message.fileName ? `Arquivo: ${message.fileName}` : 'Nova mensagem recebida'),
            `chat-${this.callcode}`
          );
        }
      });

    this.chatService.connect(this.callcode).catch(() => {
      // Falha de conexão em tempo real não impede o uso via REST (histórico já carregado).
    });
  }

  /** A aba está visível e com foco? (false = outra guia/app em primeiro plano) */
  private isActive(): boolean {
    return document.visibilityState === 'visible' && document.hasFocus();
  }

  /** Ao reganhar foco: marca como lido e limpa título/contador. */
  private handleBecameActive(): void {
    if (this.unreadWhileAway > 0 || this.messages.some((m) => !this.isMine(m))) {
      this.markAsRead();
    }
    this.unreadWhileAway = 0;
    this.browserNotification.clearTitle();
  }

  private markAsRead(): void {
    this.chatService.markAsRead(this.callcode).pipe(takeUntil(this.destroyed$)).subscribe({ error: () => {} });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
